/*
  ReferralSourceCard — small read-mostly card on the Client Details
  tab showing "How they found us" data captured from the intake form.

  Data source:
    GET /api/canonical/{email}
      → filters rows whose canonical_key starts with `client.referral.`

  The four keys we care about:
    client.referral.source          — one of the 12 channel values
    client.referral.source_other    — free text (only when source = other)
    client.referral.referrer_name   — optional referrer name
    client.referral.ad_recall       — yes / no / unsure

  Displays:
    * Channel with a friendly label (mapped from the option value)
    * Referrer name (if present)
    * Ad recall (if answered)
    * Status pill (Unreviewed / Verified / Amended / Declined)
    * "Mark verified" button when still Unreviewed

  This card is deliberately read-only for the value itself. Full
  amend / decline / defer flow lives on the Onboarding Review tab —
  we just surface the answer + a one-click verify here.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Compass, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Channel value → human label. Kept in sync with
// backend/data/onboarding_v2/*.json referral_source options.
const CHANNEL_LABELS = {
  friend_family_client: "Friend, family member or current client",
  gp_specialist: "GP or medical specialist",
  health_professional: "Physiotherapist, osteopath or another health professional",
  google: "Google search or Google Maps",
  facebook: "Facebook",
  instagram: "Instagram",
  classpass: "ClassPass",
  flyer_community: "Local flyer, school newsletter or community event",
  signage: "Walked past the studio or saw our signage",
  returning_client: "I've been a client before",
  other: "Other",
  unsure: "I'm not sure",
};

const AD_RECALL_LABELS = {
  yes_ad: "Yes — recalls seeing an ad",
  no_ad: "No — didn't see an ad",
  unsure_ad: "Not sure",
};

const REFERRAL_KEYS = [
  "client.referral.source",
  "client.referral.source_other",
  "client.referral.referrer_name",
  "client.referral.ad_recall",
];

function StatusBadge({ status }) {
  const map = {
    imported_unreviewed: { label: "Unreviewed", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    verified: { label: "Verified", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    amended: { label: "Amended", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
    declined: { label: "Declined", cls: "bg-rose-50 text-rose-800 border-rose-200" },
    deferred: { label: "Deferred", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  };
  const m = map[status] || { label: status, cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
  return <Badge className={`text-2xs border ${m.cls}`}>{m.label}</Badge>;
}

export default function ReferralSourceCard({ email }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);

  const load = useCallback(async () => {
    if (!email) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/canonical/${encodeURIComponent(email)}`);
      const referralRows = (r.data || []).filter((row) =>
        REFERRAL_KEYS.includes(row.canonical_key)
      );
      setRows(referralRows);
    } catch (e) {
      // Silent — a client with no intake yet has no rows; not an error.
      setRows([]);
    } finally { setLoading(false); }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  async function verify(row) {
    setVerifying(row.canonical_key);
    try {
      await api.post(
        `/canonical/${encodeURIComponent(email)}/${encodeURIComponent(row.canonical_key)}/verify`,
        {},
      );
      toast.success("Marked verified");
      await load();
    } catch (e) {
      toast.error("Verify failed", { description: e?.response?.data?.detail || e.message });
    } finally { setVerifying(null); }
  }

  if (loading) {
    return (
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading referral source…
        </div>
      </Card>
    );
  }

  if (!rows.length) {
    // Only render an empty state when there's no data at all — keeps
    // the client page tidy for clients registered pre-2026-02.
    return (
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-zinc-100 border border-zinc-200 text-zinc-500">
            <Compass className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-zinc-800">How they found us</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              This client submitted their intake before the referral question was added, so
              we don't have this data. New submissions will show up here automatically.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const byKey = Object.fromEntries(rows.map((r) => [r.canonical_key, r]));
  const source = byKey["client.referral.source"];
  const sourceOther = byKey["client.referral.source_other"];
  const referrer = byKey["client.referral.referrer_name"];
  const adRecall = byKey["client.referral.ad_recall"];

  function displayChannel() {
    if (!source?.value) return null;
    const label = CHANNEL_LABELS[source.value] || source.value;
    if (source.value === "other" && sourceOther?.value) {
      return `Other — ${sourceOther.value}`;
    }
    return label;
  }

  return (
    <Card className="p-4 rounded-lg border border-zinc-200 shadow-none"
          data-testid="referral-source-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800">
            <Compass className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-zinc-800">How they found us</div>
            <div className="text-xs text-zinc-500">From the referral question on the intake form.</div>
          </div>
        </div>
        <a
          href={`/clients/${encodeURIComponent(email)}?tab=onboarding-review`}
          className="text-2xs text-indigo-700 hover:underline inline-flex items-center gap-1"
          data-testid="referral-source-open-review"
          title="Open the full Onboarding Review tab to amend / decline / add notes"
        >
          Review <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="mt-3 space-y-2 text-sm">
        {source && (
          <div className="flex items-start gap-2">
            <div className="w-32 text-2xs uppercase tracking-wider text-zinc-500 pt-0.5">Channel</div>
            <div className="flex-1">
              <div className="text-zinc-900" data-testid="referral-channel-value">{displayChannel() || <span className="text-zinc-400">—</span>}</div>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={source.status} />
                {source.status === "imported_unreviewed" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-2xs"
                    onClick={() => verify(source)}
                    disabled={verifying === source.canonical_key}
                    data-testid="referral-verify-channel"
                  >
                    {verifying === source.canonical_key
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <CheckCircle2 className="h-3 w-3 mr-1" />}
                    Mark verified
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {referrer?.value && (
          <div className="flex items-start gap-2">
            <div className="w-32 text-2xs uppercase tracking-wider text-zinc-500 pt-0.5">Referred by</div>
            <div className="flex-1 text-zinc-900" data-testid="referral-referrer-value">
              {referrer.value}
            </div>
          </div>
        )}

        {adRecall?.value && (
          <div className="flex items-start gap-2">
            <div className="w-32 text-2xs uppercase tracking-wider text-zinc-500 pt-0.5">Ad recall</div>
            <div className="flex-1 text-zinc-900" data-testid="referral-ad-recall-value">
              {AD_RECALL_LABELS[adRecall.value] || adRecall.value}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
